<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import FileUpload from 'vue-upload-component';
import { Dropdown } from 'floating-vue';
import { formatBytes } from 'shared/helpers/FileHelper';
import { useAlert } from 'dashboard/composables';

import Avatar from 'next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import WhatsAppEmojiPicker from 'dashboard/components/widgets/conversation/WhatsAppEmojiPicker.vue';
import { MAX_ATTACHMENT_COUNT } from 'dashboard/constants/attachments';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  attachments: {
    type: Array,
    default: () => [],
  },
  conversation: {
    type: Object,
    default: () => ({}),
  },
  contact: {
    type: Object,
    default: () => ({}),
  },
  mode: {
    type: String,
    default: 'compact',
    validator: value => ['compact', 'gallery'].includes(value),
  },
  accept: {
    type: String,
    default: '',
  },
  enableMultipleFileUpload: {
    type: Boolean,
    default: true,
  },
  onAddMore: {
    type: Function,
    default: null,
  },
  isSending: {
    type: Boolean,
    default: false,
  },
  sendProgress: {
    type: Object,
    default: () => ({ current: 0, total: 0 }),
  },
  maxCaptionLength: {
    type: Number,
    default: 1024,
  },
});

const emit = defineEmits(['removeAttachment', 'updateCaption', 'send']);

const nonRecordedAudioAttachments = computed(() =>
  props.attachments.filter(attachment => !attachment?.isRecordedAudio)
);

const recordedAudioAttachments = computed(() =>
  props.attachments.filter(attachment => attachment.isRecordedAudio)
);

const selectedIndex = ref(0);
const captionTextareaRef = ref(null);
const captionSelection = ref({ start: 0, end: 0 });
const showEmojiPicker = ref(false);
const emojiPickerTriggerRef = ref(null);

const safeSelectedIndex = computed(() => {
  const list = nonRecordedAudioAttachments.value;
  if (!list.length) return 0;
  if (selectedIndex.value >= list.length) return list.length - 1;
  if (selectedIndex.value < 0) return 0;
  return selectedIndex.value;
});

const selectedAttachment = computed(
  () => nonRecordedAudioAttachments.value[safeSelectedIndex.value] || null
);

const attachmentCount = computed(() => props.attachments.length);

const galleryAttachmentCount = computed(
  () => nonRecordedAudioAttachments.value.length
);

const galleryProgressLabel = computed(
  () => `${safeSelectedIndex.value + 1}/${galleryAttachmentCount.value}`
);

const conversationSender = computed(
  () => props.conversation?.meta?.sender || {}
);

const conversationName = computed(() => {
  const contact = props.contact || {};
  const sender = conversationSender.value;
  return (
    contact.name ||
    contact.available_name ||
    sender.name ||
    sender.available_name ||
    ''
  );
});

const conversationAvatar = computed(() => {
  const contact = props.contact || {};
  const sender = conversationSender.value;
  return (
    contact.thumbnail ||
    contact.avatar_url ||
    sender.thumbnail ||
    sender.avatar_url ||
    ''
  );
});

const conversationSecondaryInfo = computed(() => {
  const contact = props.contact || {};
  const sender = conversationSender.value;
  return [
    contact.phone_number,
    contact.email,
    contact.identifier,
    sender.phone_number,
    sender.email,
    sender.identifier,
  ].find(Boolean);
});

const previewSource = computed(() => selectedAttachment.value?.thumb || '');

const downloadSource = computed(
  () => selectedAttachment.value?.originalFileUrl || previewSource.value
);

const previewName = computed(() =>
  selectedAttachment.value ? fileName(selectedAttachment.value.resource) : ''
);

const previewIsImage = computed(() =>
  previewSource.value && selectedAttachment.value
    ? isTypeImage(selectedAttachment.value.resource)
    : false
);

const previewIsVideo = computed(() =>
  previewSource.value && selectedAttachment.value
    ? isTypeVideo(selectedAttachment.value.resource)
    : false
);

const previewIsPdf = computed(() => hasPdfPreview(selectedAttachment.value));

const hasCaption = computed(() => !!selectedAttachment.value?.caption);

const captionLength = computed(
  () => selectedAttachment.value?.caption?.length || 0
);

const showCaptionCounter = computed(
  () => captionLength.value >= props.maxCaptionLength * 0.8
);

const captionCounterLabel = computed(
  () => `${captionLength.value}/${props.maxCaptionLength}`
);

const sendProgressLabel = computed(
  () =>
    `${props.sendProgress.active || props.sendProgress.current}/${props.sendProgress.total}`
);

const sendProgressPercent = computed(() => {
  if (!props.sendProgress.total) return 0;
  return Math.min(
    100,
    Math.round((props.sendProgress.current / props.sendProgress.total) * 100)
  );
});

const sendProgressStatusLabel = computed(
  () => `${sendProgressLabel.value} · ${sendProgressPercent.value}%`
);

const formatFileSize = file => {
  const size = file.byte_size || file.size || 0;
  return formatBytes(size, 0);
};

const fileName = file => file.filename || file.name || '';

const isTypeImage = file => {
  const type = file.content_type || file.type || '';
  if (type.includes('image')) return true;
  return /\.(avif|gif|jpe?g|png|svg|webp)$/i.test(fileName(file));
};

const isTypeVideo = file => {
  const type = file.content_type || file.type || '';
  if (type.includes('video')) return true;
  return /\.(m4v|mov|mp4|webm)$/i.test(fileName(file));
};

const isTypePdf = file => {
  const type = file.content_type || file.type || '';
  if (type.toLowerCase() === 'application/pdf') return true;
  return /\.pdf$/i.test(fileName(file));
};

const hasPdfPreview = attachment =>
  (attachment?.isPdfPreview || isTypePdf(attachment?.resource || {})) &&
  /^data:image\//i.test(attachment?.thumb || '');

const downloadPreview = () => {
  if (!downloadSource.value || props.isSending) return;
  const link = document.createElement('a');
  link.href = downloadSource.value;
  link.download = previewName.value || 'media';
  link.rel = 'noopener';
  link.click();
};

const copyPreview = async () => {
  if (!previewSource.value || !previewIsImage.value || props.isSending) return;

  try {
    const ClipboardItemConstructor = window.ClipboardItem;
    if (!navigator.clipboard?.write || !ClipboardItemConstructor) {
      throw new Error('Clipboard API unavailable');
    }
    const response = await fetch(previewSource.value);
    const blob = await response.blob();
    await navigator.clipboard.write([
      new ClipboardItemConstructor({ [blob.type]: blob }),
    ]);
    useAlert('Imagem copiada.');
  } catch {
    useAlert('Não foi possível copiar a imagem.');
  }
};

const hideEmojiPicker = () => {
  showEmojiPicker.value = false;
};

const getEmojiPickerTrigger = () => emojiPickerTriggerRef.value;

const rememberCaptionSelection = event => {
  const element = event?.target || captionTextareaRef.value;
  if (!element) return;
  captionSelection.value = {
    start: element.selectionStart ?? 0,
    end: element.selectionEnd ?? element.selectionStart ?? 0,
  };
};

const resizeCaptionInput = () => {
  nextTick(() => {
    const element = captionTextareaRef.value;
    if (!element) return;
    element.style.height = '2.75rem';
    element.style.height = `${Math.min(element.scrollHeight, 88)}px`;
  });
};

const focusCaptionAt = position => {
  nextTick(() => {
    const element = captionTextareaRef.value;
    if (!element) return;
    resizeCaptionInput();
    captionSelection.value = { start: position, end: position };
    element.focus();
    element.setSelectionRange(position, position);
  });
};

const selectThumb = index => {
  if (props.isSending) return;
  selectedIndex.value = index;
  hideEmojiPicker();
  focusCaptionAt(
    nonRecordedAudioAttachments.value[index]?.caption?.length || 0
  );
};

const onRemoveAttachment = itemIndex => {
  if (props.isSending) return;
  emit(
    'removeAttachment',
    nonRecordedAudioAttachments.value
      .filter((_, index) => index !== itemIndex)
      .concat(recordedAudioAttachments.value)
  );
};

const discardAll = () => {
  if (props.isSending) return;
  emit('removeAttachment', recordedAudioAttachments.value);
};

const onCaptionInput = event => {
  emit('updateCaption', {
    index: safeSelectedIndex.value,
    caption: event.target.value,
  });
  rememberCaptionSelection(event);
  resizeCaptionInput();
};

const clearCaption = () => {
  if (props.isSending) return;
  emit('updateCaption', { index: safeSelectedIndex.value, caption: '' });
  focusCaptionAt(0);
};

const onGalleryAddMore = file => {
  if (!file || props.isSending) return;
  if (attachmentCount.value >= MAX_ATTACHMENT_COUNT) {
    useAlert(
      `Você pode enviar no máximo ${MAX_ATTACHMENT_COUNT} arquivos por vez.`
    );
    return;
  }
  props.onAddMore?.(file);
};

const toggleEmojiPicker = () => {
  if (props.isSending) return;
  const shouldOpen = !showEmojiPicker.value;
  if (shouldOpen) {
    emitter.emit(BUS_EVENTS.EMOJI_PICKER_OPENED, { owner: 'attachments' });
  }
  showEmojiPicker.value = shouldOpen;
};

const onEmojiPickerOpened = ({ owner } = {}) => {
  if (owner !== 'attachments') showEmojiPicker.value = false;
};

const onEmojiSelect = emoji => {
  const current = selectedAttachment.value?.caption || '';
  const element = captionTextareaRef.value;
  const { start, end } = element
    ? {
        start: element.selectionStart ?? captionSelection.value.start,
        end: element.selectionEnd ?? captionSelection.value.end,
      }
    : captionSelection.value;
  const availableLength =
    props.maxCaptionLength - current.length + Math.max(0, end - start);
  if (!emoji || emoji.length > availableLength) return;
  const emojiToInsert = emoji;

  const caption = `${current.slice(0, start)}${emojiToInsert}${current.slice(
    end
  )}`;
  const cursorPosition = start + emojiToInsert.length;

  emit('updateCaption', {
    index: safeSelectedIndex.value,
    caption,
  });
  focusCaptionAt(cursorPosition);
};

const onSend = () => {
  if (props.isSending || !selectedAttachment.value) return;
  emit('send');
};

const onEnterSend = event => {
  if (event.shiftKey || props.isSending) return;
  event.preventDefault();
  emit('send');
};

const handleGlobalEnter = event => {
  if (props.mode !== 'gallery' || event.key !== 'Enter') return;
  if (event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) return;
  if (props.isSending || showEmojiPicker.value || !selectedAttachment.value) {
    return;
  }

  const active = document.activeElement;
  if (active) {
    if (active.classList?.contains('caption-pill-input')) return;
    if (active.tagName === 'TEXTAREA' || active.isContentEditable) return;
    if (active.tagName === 'INPUT') {
      const type = (active.getAttribute('type') || 'text').toLowerCase();
      const textInputTypes = [
        'text',
        'search',
        'email',
        'url',
        'tel',
        'password',
        'number',
      ];
      if (textInputTypes.includes(type)) return;
    }
  }

  event.preventDefault();
  emit('send');
};

watch(
  () => nonRecordedAudioAttachments.value.length,
  (newLength, oldLength) => {
    if (newLength > oldLength) {
      selectedIndex.value = newLength - 1;
    } else if (selectedIndex.value >= newLength) {
      selectedIndex.value = Math.max(0, newLength - 1);
    }
  }
);

watch(
  () => safeSelectedIndex.value,
  () => resizeCaptionInput()
);

onMounted(() => {
  window.addEventListener('keydown', handleGlobalEnter, true);
  emitter.on(BUS_EVENTS.EMOJI_PICKER_OPENED, onEmojiPickerOpened);
  resizeCaptionInput();
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleGlobalEnter, true);
  emitter.off(BUS_EVENTS.EMOJI_PICKER_OPENED, onEmojiPickerOpened);
});
</script>

<template>
  <Teleport v-if="mode === 'gallery'" to=".conversation-details-wrap">
    <section
      class="aceleres-media-gallery absolute inset-0 z-[200] flex min-h-0 flex-col overflow-hidden bg-n-background"
      role="dialog"
      aria-modal="true"
    >
      <header
        class="aceleres-media-gallery__conversation-header flex min-h-16 flex-shrink-0 items-center gap-3 border-b border-n-weak bg-n-background px-3 py-2 sm:px-5"
      >
        <Avatar
          :name="conversationName"
          :src="conversationAvatar"
          :size="40"
          rounded-full
          class="flex-shrink-0"
        />
        <div class="min-w-0 flex-1 overflow-hidden">
          <h2
            class="truncate text-sm font-semibold leading-5 text-n-slate-12 sm:text-base"
            :title="conversationName"
          >
            {{ conversationName }}
          </h2>
          <p
            v-if="conversationSecondaryInfo"
            class="truncate text-xs leading-5 text-n-slate-11"
            :title="conversationSecondaryInfo"
          >
            {{ conversationSecondaryInfo }}
          </p>
        </div>
        <span
          v-if="galleryAttachmentCount > 1"
          class="flex flex-shrink-0 items-center gap-1 rounded-full bg-n-alpha-2 px-2.5 py-1 text-xs font-medium text-n-slate-12"
          aria-live="polite"
        >
          <i class="i-lucide-images size-4 text-n-slate-11" />
          {{ galleryProgressLabel }}
        </span>
      </header>

      <div
        class="aceleres-media-gallery__toolbar relative flex h-14 flex-shrink-0 items-center border-b border-n-weak bg-n-background px-3 sm:px-5"
      >
        <button
          type="button"
          class="inline-flex size-10 flex-shrink-0 items-center justify-center rounded-full text-n-slate-12 transition-colors hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-40"
          :aria-label="$t('GENERAL.CLOSE')"
          :title="$t('GENERAL.CLOSE')"
          :disabled="isSending"
          @click="discardAll"
        >
          <Icon icon="i-lucide-x" class="size-6" />
        </button>

        <div
          class="aceleres-media-gallery__header-actions ml-auto flex flex-shrink-0 items-center gap-0.5"
        >
          <button
            v-if="previewIsImage"
            type="button"
            class="hidden size-10 items-center justify-center rounded-full text-n-slate-12 transition-colors hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-30 sm:inline-flex"
            aria-label="Copiar imagem"
            title="Copiar imagem"
            :disabled="isSending || !previewSource"
            @click="copyPreview"
          >
            <Icon icon="i-lucide-copy" class="size-5" />
          </button>
          <button
            type="button"
            class="inline-flex size-10 items-center justify-center rounded-full text-n-slate-12 transition-colors hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-30"
            :aria-label="$t('CONVERSATION.DOWNLOAD')"
            :title="$t('CONVERSATION.DOWNLOAD')"
            :disabled="isSending || !downloadSource"
            @click="downloadPreview"
          >
            <Icon icon="i-lucide-download" class="size-5" />
          </button>
        </div>
      </div>

      <div
        class="aceleres-media-gallery__body flex min-h-0 flex-1 flex-col overflow-hidden bg-n-background"
      >
        <div
          class="aceleres-media-gallery__stage flex min-h-0 flex-1 items-center justify-center p-2 sm:p-5 lg:p-6"
        >
          <div
            v-if="selectedAttachment"
            class="aceleres-media-gallery__canvas flex size-full max-w-[64rem] items-center justify-center overflow-hidden rounded-lg bg-transparent sm:max-w-[72rem]"
          >
            <div
              v-if="previewIsPdf"
              class="flex size-full min-h-0 flex-col items-center gap-3"
            >
              <p
                class="max-w-full flex-shrink-0 truncate px-4 text-center text-sm font-medium text-n-slate-12"
                :title="previewName"
              >
                {{ previewName }}
              </p>
              <p
                v-if="selectedAttachment.pdfPageCount"
                class="flex-shrink-0 text-center text-xs text-n-slate-10"
              >
                {{
                  $t('CONVERSATION.PDF_VIEWER.PAGE_COUNT', {
                    count: selectedAttachment.pdfPageCount,
                  })
                }}
              </p>
              <img
                :key="previewSource"
                class="min-h-0 max-h-full max-w-full flex-1 object-contain transition-transform duration-150 ease-out"
                :src="previewSource"
                :alt="previewName"
              />
            </div>
            <img
              v-else-if="previewIsImage"
              :key="previewSource"
              class="max-h-full max-w-full object-contain transition-transform duration-150 ease-out"
              :src="previewSource"
              :alt="previewName"
            />
            <video
              v-else-if="previewIsVideo"
              :key="previewSource"
              controls
              class="max-h-full max-w-full object-contain"
              :src="previewSource"
            />
            <div
              v-else
              class="flex max-w-sm flex-col items-center gap-3 rounded-lg bg-n-background p-8 text-center shadow-sm"
            >
              <i
                :class="
                  selectedAttachment.isPdfPreviewLoading
                    ? 'i-lucide-loader-circle animate-spin'
                    : 'i-lucide-file'
                "
                class="size-12 text-n-slate-10"
              />
              <span
                class="max-w-full truncate text-sm font-medium text-n-slate-12"
              >
                {{ fileName(selectedAttachment.resource) }}
              </span>
              <span
                v-if="selectedAttachment.isPdfPreviewLoading"
                class="text-xs text-n-slate-10"
              >
                {{ $t('CONVERSATION.GENERATING_PDF_PREVIEW') }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{ formatFileSize(selectedAttachment.resource) }}
              </span>
            </div>
          </div>
        </div>

        <div
          v-if="selectedAttachment"
          class="aceleres-media-gallery__caption-wrap flex-shrink-0 px-2 pb-2 sm:px-6 sm:pb-4"
        >
          <div
            class="aceleres-media-gallery__caption relative mx-auto flex w-full max-w-[48rem] items-center gap-1 rounded-lg border border-n-weak bg-n-background px-2 py-1 shadow-sm transition-shadow focus-within:border-n-brand focus-within:shadow-md sm:items-end sm:gap-2 sm:px-4 sm:py-2"
          >
            <textarea
              ref="captionTextareaRef"
              rows="1"
              :value="selectedAttachment.caption || ''"
              :maxlength="maxCaptionLength"
              :disabled="isSending"
              :placeholder="$t('CONVERSATION.FOOTER.MSG_INPUT')"
              class="caption-pill-input min-w-0 flex-1 resize-none overflow-y-auto border-0 bg-transparent px-2 py-2 text-sm leading-5 text-n-slate-12 outline-none placeholder:text-n-slate-9 focus:border-0 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-60 sm:text-base sm:leading-6"
              @blur="rememberCaptionSelection"
              @click="rememberCaptionSelection"
              @input="onCaptionInput"
              @keyup="rememberCaptionSelection"
              @select="rememberCaptionSelection"
              @keydown.enter="onEnterSend"
            />

            <div class="flex flex-shrink-0 items-center gap-1 pb-0.5">
              <span
                v-if="showCaptionCounter"
                class="px-1 text-xs tabular-nums text-n-slate-10"
              >
                {{ captionCounterLabel }}
              </span>

              <button
                v-if="hasCaption"
                type="button"
                class="inline-flex size-9 items-center justify-center rounded-full text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 disabled:opacity-40"
                :aria-label="$t('GENERAL.DISCARD')"
                :title="$t('GENERAL.DISCARD')"
                :disabled="isSending"
                @click="clearCaption"
              >
                <Icon icon="i-lucide-x" class="size-5" />
              </button>

              <div class="relative">
                <button
                  ref="emojiPickerTriggerRef"
                  type="button"
                  class="inline-flex size-10 items-center justify-center rounded-full text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 disabled:opacity-40"
                  :class="{ 'bg-n-alpha-2 text-n-brand': showEmojiPicker }"
                  :aria-label="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
                  :title="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
                  :disabled="isSending"
                  @mousedown.prevent
                  @click.stop="toggleEmojiPicker"
                >
                  <i class="i-lucide-smile size-6" />
                </button>

                <Dropdown
                  v-if="showEmojiPicker"
                  :shown="showEmojiPicker"
                  :triggers="[]"
                  :popper-triggers="[]"
                  :reference-node="getEmojiPickerTrigger"
                  placement="bottom-end"
                  :distance="8"
                  :overflow-padding="8"
                  flip
                  shift
                  shift-cross-axis
                  strategy="fixed"
                  handle-resize
                  :auto-hide="false"
                  popper-class="acl-attachment-emoji-popper"
                >
                  <span class="absolute size-0 overflow-hidden" aria-hidden="true" />
                  <template #popper>
                    <div v-on-clickaway="hideEmojiPicker" @click.stop>
                      <WhatsAppEmojiPicker
                        embedded
                        :on-click="onEmojiSelect"
                      />
                    </div>
                  </template>
                </Dropdown>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer
        class="aceleres-media-gallery__footer relative flex min-h-[4.5rem] flex-shrink-0 items-center gap-2 border-t border-n-weak bg-n-background px-2 py-1.5 sm:min-h-20 sm:gap-3 sm:px-6 sm:py-2 md:min-h-28"
      >
        <div
          v-if="isSending && sendProgress.total"
          class="absolute inset-x-0 top-0 h-1 overflow-hidden bg-n-alpha-3"
          role="progressbar"
          :aria-label="$t('CANNED_MGMT.ATTACHMENT.UPLOADING')"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-valuenow="sendProgressPercent"
        >
          <div
            class="h-full bg-n-brand transition-[width] duration-300"
            :style="{ width: `${sendProgressPercent}%` }"
          />
        </div>

        <div
          class="aceleres-media-gallery__thumb-rail flex min-w-0 flex-1 items-center justify-start gap-2 overflow-x-auto py-1 md:justify-center"
        >
          <div
            v-for="(attachment, index) in nonRecordedAudioAttachments"
            :key="attachment.id || index"
            class="group relative size-12 flex-shrink-0 overflow-visible sm:size-16"
          >
            <button
              type="button"
              class="relative size-full overflow-hidden rounded-lg border-2 bg-n-alpha-2 p-0.5 transition"
              :class="
                index === safeSelectedIndex
                  ? 'border-n-brand shadow-sm ring-2 ring-n-brand/20'
                  : 'border-transparent hover:border-n-strong'
              "
              :aria-label="fileName(attachment.resource)"
              :disabled="isSending"
              @click="selectThumb(index)"
            >
              <span class="block size-full overflow-hidden rounded-lg">
                <img
                  v-if="
                    isTypeImage(attachment.resource) || hasPdfPreview(attachment)
                  "
                  class="size-full object-cover"
                  :src="attachment.thumb"
                  :alt="fileName(attachment.resource)"
                />
                <video
                  v-else-if="isTypeVideo(attachment.resource)"
                  muted
                  class="size-full object-cover"
                  :src="attachment.thumb"
                />
                <span
                  v-else
                  class="flex size-full items-center justify-center bg-n-alpha-2"
                >
                  <i class="i-lucide-file size-6 text-n-slate-10" />
                </span>
              </span>

              <span
                v-if="attachment.caption"
                class="absolute bottom-1 left-1 size-2 rounded-full bg-[#00a884] ring-2 ring-n-background"
                aria-hidden="true"
              />
            </button>

            <button
              type="button"
              class="absolute -right-1.5 -top-1.5 z-10 inline-flex size-6 items-center justify-center rounded-full border-2 border-n-background bg-n-slate-12 text-white shadow-md transition hover:bg-n-slate-11 disabled:opacity-40"
              :aria-label="$t('GENERAL.DISCARD')"
              :title="$t('GENERAL.DISCARD')"
              :disabled="isSending"
              @click.stop="onRemoveAttachment(index)"
            >
              <Icon icon="i-lucide-x" class="size-4 !text-white" />
            </button>
          </div>

          <FileUpload
            v-if="
              onAddMore &&
              !isSending &&
              attachmentCount < MAX_ATTACHMENT_COUNT
            "
            input-id="conversationAttachmentGalleryAdd"
            :size="4096 * 4096"
            :accept="accept || undefined"
            :multiple="enableMultipleFileUpload"
            :maximum="MAX_ATTACHMENT_COUNT"
            drop=".conversation-details-wrap.chat-whats"
            :drop-directory="false"
            :data="{
              direct_upload_url: '/rails/active_storage/direct_uploads',
              direct_upload: true,
            }"
            class="flex-shrink-0"
            @input-file="onGalleryAddMore"
          >
            <div
              class="aceleres-media-gallery__add flex size-12 items-center justify-center rounded-lg border border-dashed border-n-strong bg-n-alpha-1 text-n-slate-11 transition-colors hover:border-n-brand hover:bg-n-alpha-2 hover:text-n-brand sm:size-16"
              :title="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
            >
              <i class="i-lucide-plus size-6" />
            </div>
          </FileUpload>
        </div>

        <div
          v-if="isSending && sendProgress.total"
          class="flex-shrink-0 text-xs font-medium tabular-nums text-n-slate-10"
        >
          {{ sendProgressStatusLabel }}
        </div>

        <div class="relative flex flex-shrink-0 flex-col items-center gap-1">
          <span
            v-if="attachmentCount > 1"
            class="absolute -right-1 -top-1 z-10 hidden min-h-5 min-w-5 items-center justify-center rounded-full bg-white px-1 text-[0.6875rem] font-bold leading-5 text-n-slate-12 shadow-sm ring-1 ring-n-weak md:inline-flex"
            aria-live="polite"
            :aria-label="`${attachmentCount} arquivos selecionados`"
          >
            {{ attachmentCount }}
          </span>
          <button
            type="button"
            class="aceleres-media-gallery__send inline-flex size-11 items-center justify-center rounded-full bg-[#00a884] text-white shadow-md transition hover:bg-[#008f6f] hover:shadow-lg active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-[#00a884] sm:size-12 md:size-16"
            :aria-label="$t('CONVERSATION.REPLYBOX.SEND')"
            :title="$t('CONVERSATION.REPLYBOX.SEND')"
            :disabled="isSending || !selectedAttachment"
            @click="onSend"
          >
            <i
              :class="
                isSending
                  ? 'i-lucide-loader-circle animate-spin'
                  : 'i-lucide-send-horizontal'
              "
              class="size-6"
            />
          </button>
        </div>
      </footer>
    </section>
  </Teleport>

  <div
    v-if="mode !== 'gallery'"
    class="flex max-h-[12.5rem] flex-wrap gap-x-2 gap-y-1 overflow-auto"
  >
    <div
      v-for="(attachment, index) in nonRecordedAudioAttachments"
      :key="attachment.id || index"
      class="flex w-[15rem] items-center gap-1 rounded-md bg-n-slate-3 p-1"
    >
      <div class="flex w-6 max-w-[4rem] flex-shrink-0 items-center">
        <img
          v-if="
            isTypeImage(attachment.resource) || hasPdfPreview(attachment)
          "
          class="size-6 rounded-sm object-cover"
          :src="attachment.thumb"
          :alt="fileName(attachment.resource)"
        />
        <span v-else class="relative -top-px size-6 text-left text-lg">
          <i class="i-lucide-file size-5" />
        </span>
      </div>
      <div class="min-w-[50%] max-w-3/5 overflow-hidden text-ellipsis">
        <span
          class="h-4 overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium"
        >
          {{ fileName(attachment.resource) }}
        </span>
      </div>
      <div class="w-[30%] justify-center">
        <span class="overflow-hidden text-ellipsis whitespace-nowrap text-xs">
          {{ formatFileSize(attachment.resource) }}
        </span>
      </div>
      <div class="flex items-center justify-center">
        <Button
          ghost
          slate
          xs
          icon="i-lucide-x"
          @click="onRemoveAttachment(index)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.caption-pill-input {
  height: 2.75rem;
  min-height: 2.75rem;
  max-height: 5.5rem;
  margin: 0;
}

.caption-pill-input:hover,
.caption-pill-input:focus,
.caption-pill-input:focus-visible {
  border: 0 !important;
  outline: none !important;
  box-shadow: none !important;
}

.aceleres-media-gallery__footer {
  padding-bottom: max(0.375rem, env(safe-area-inset-bottom));
}

.aceleres-media-gallery__thumb-rail {
  scrollbar-width: none;
}

.aceleres-media-gallery__thumb-rail::-webkit-scrollbar {
  display: none;
}

@media (max-width: 767px) {
  .aceleres-media-gallery {
    --attachment-safe-top: env(safe-area-inset-top, 0px);
    --attachment-safe-bottom: env(safe-area-inset-bottom, 0px);
    width: 100%;
    height: 100%;
    max-height: 100%;
    padding-top: var(--attachment-safe-top);
  }

  .aceleres-media-gallery__conversation-header {
    width: 100%;
    min-height: 4rem;
    padding: 0.5rem;
  }

  .aceleres-media-gallery__toolbar > button,
  .aceleres-media-gallery__header-actions > button {
    width: 2.5rem;
    height: 2.5rem;
    min-width: 2.5rem;
    padding: 0;
  }

  .aceleres-media-gallery__header-actions {
    margin-left: auto;
  }

  .aceleres-media-gallery__body {
    width: 100%;
    min-height: 0;
  }

  .aceleres-media-gallery__stage {
    width: 100%;
    min-height: 7rem;
    padding: 0.5rem;
  }

  .aceleres-media-gallery__canvas {
    width: 100%;
    height: 100%;
    border-radius: 0.375rem;
  }

  .aceleres-media-gallery__canvas img,
  .aceleres-media-gallery__canvas video {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .aceleres-media-gallery__caption-wrap {
    padding: 0 0.5rem 0.5rem;
  }

  .aceleres-media-gallery__caption {
    min-height: 3rem;
    padding: 0.125rem 0.375rem;
    border-radius: 1.5rem;
  }

  .aceleres-media-gallery__caption .caption-pill-input {
    max-height: 4rem;
    padding-block: 0.625rem;
    font-size: 1rem;
  }

  .aceleres-media-gallery__footer {
    width: 100%;
    min-height: calc(4.75rem + var(--attachment-safe-bottom));
    padding: 0.25rem 0.5rem max(0.25rem, var(--attachment-safe-bottom));
    gap: 0.5rem;
  }

  .aceleres-media-gallery__thumb-rail {
    min-width: 0;
    padding: 0.5rem 0.25rem 0.25rem;
    gap: 0.5rem;
    justify-content: flex-start;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    scroll-snap-type: x proximity;
  }

  .aceleres-media-gallery__thumb-rail > .group,
  .aceleres-media-gallery__add {
    width: 3.25rem;
    height: 3.25rem;
    min-width: 3.25rem;
    scroll-snap-align: center;
  }

  .aceleres-media-gallery__send {
    width: 3rem;
    height: 3rem;
    min-width: 3rem;
  }

}
</style>
